/**
 * MainLayout Component
 *
 * Main application shell with fixed header, collapsible sidebar,
 * scrollable content area, and footer.
 * Follows the same layout structure as the CSP (Customer Success Portal).
 */
import { useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { AppHeader } from './AppHeader';
import { SideNav } from './SideNav';
import { AppFooter } from './AppFooter';
import { AuditorBanner } from '../common/AuditorBanner';
import { GovernancePlaneHeader } from './GovernancePlaneHeader';
import { SystemTelemetryBar } from '../system/SystemTelemetryBar';
import { useAppStore } from '../../stores/appStore';
import { useBreakpoints, BREAKPOINTS } from '../../hooks/useBreakpoints';

const HEADER_HEIGHT = '48px';
const SIDENAV_WIDTH = '240px';
const SIDENAV_COLLAPSED_WIDTH = '48px';

const useStyles = makeStyles({
  layout: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    backgroundColor: tokens.colorNeutralBackground2,
    overflow: 'hidden',
  },
  container: {
    display: 'flex',
    flex: 1,
    marginTop: HEADER_HEIGHT,
    overflow: 'hidden',
  },
  main: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    marginLeft: SIDENAV_WIDTH,
    transition: 'margin-left 0.2s ease',
    backgroundColor: tokens.colorNeutralBackground3,
    overflow: 'hidden',
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
    },
  },
  mainCollapsed: {
    marginLeft: SIDENAV_COLLAPSED_WIDTH,
  },
  mainMobile: {
    marginLeft: 0,
  },
  scrollArea: {
    flex: 1,
    overflowY: 'auto',
  },
  content: {
    maxWidth: '1400px',
    width: '100%',
    margin: '0 auto',
    paddingLeft: tokens.spacingHorizontalXXXL,
    paddingRight: tokens.spacingHorizontalXXXL,
    paddingTop: tokens.spacingVerticalXXL,
    paddingBottom: tokens.spacingVerticalXXL,
    minHeight: 0,
  },
});

export function MainLayout() {
  const styles = useStyles();
  const { isSideNavCollapsed, isMobile, setIsMobile, setSideNavCollapsed } = useAppStore();
  const { width } = useBreakpoints();

  // Handle responsive behavior
  useEffect(() => {
    const mobile = width < BREAKPOINTS.mobile;
    const tablet = width < BREAKPOINTS.tablet;

    setIsMobile(mobile);

    if (mobile) {
      setSideNavCollapsed(true);
    } else if (tablet && !isSideNavCollapsed) {
      setSideNavCollapsed(true);
    }
  }, [width, setIsMobile, setSideNavCollapsed, isSideNavCollapsed]);

  const mainClassName = mergeClasses(
    styles.main,
    isSideNavCollapsed && styles.mainCollapsed,
    isMobile && styles.mainMobile
  );

  return (
    <div className={styles.layout}>
      <AppHeader />
      <div className={styles.container}>
        <SideNav />
        <main className={mainClassName} role="main">
          <GovernancePlaneHeader />
          <SystemTelemetryBar />
          <AuditorBanner />
          <div className={styles.scrollArea}>
            <div className={styles.content}>
              <Outlet />
            </div>
          </div>
          <AppFooter />
        </main>
      </div>
    </div>
  );
}

export default MainLayout;
